function filenameFromDisposition(header: string | null): string | undefined {
  if (!header) return;
  const utf = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf?.[1]) return decodeURIComponent(utf[1]);
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const bare = header.match(/filename=([^;]+)/i);
  return bare?.[1]?.trim();
}

async function fetchPresignedPdf(url: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to load label');
  const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' });
  return { blob, disposition: res.headers.get('Content-Disposition') };
}

/** Fetch a presigned S3 object and save it. Cross-origin `<a download>` cannot do this. */
export async function downloadPresignedPdf(url: string, filename?: string) {
  const { blob, disposition } = await fetchPresignedPdf(url);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename || filenameFromDisposition(disposition) || 'label.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Print via a same-origin blob iframe. A presigned S3 src is cross-origin, so iframe.print() is blocked. */
export async function printPresignedPdf(url: string) {
  const { blob } = await fetchPresignedPdf(url);
  const objectUrl = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = objectUrl;

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    iframe.remove();
    URL.revokeObjectURL(objectUrl);
  };

  let printed = false;
  const tryPrint = () => {
    if (printed) return;
    const win = iframe.contentWindow;
    if (!win) return;
    printed = true;
    try {
      win.focus();
      win.print();
    } finally {
      win.addEventListener('afterprint', cleanup);
      window.setTimeout(cleanup, 60_000);
    }
  };

  iframe.addEventListener('load', tryPrint);
  document.body.appendChild(iframe);
  window.setTimeout(tryPrint, 750);
}
